import { z } from 'zod'

import {
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  moneyPaisa,
  noteSchema,
  positivePaisaSchema,
  versionSchema,
} from '../common'

/**
 * A36 · `GET/POST/PATCH/DELETE /feed-plans` — the daily rate over a date range.
 *
 * **Feed cost is never stored as rows** (BR §5). There is no job writing a feed
 * expense per animal per day; there is a rate and a range, and the cost is
 * derived from them at read time. That is the whole reason a corrected rate is
 * retroactive and exact rather than a migration.
 *
 * `animalId: null` is the herd-wide rate. An animal-specific plan wins over it
 * for the days they overlap, later starts win over earlier ones, and adjacent
 * equal rates merge (§4.2).
 */
export const feedPlanSchema = z.object({
  id: idSchema,
  animalId: idSchema.nullish(),
  animalLabel: z.string().nullish(),
  /**
   * The column is `rate_paisa_per_day`, but the **wire key ends in `Paisa`**
   * like every other amount. That suffix is not cosmetic: the money serialiser
   * and both frontends' redaction strip by it, so a key named
   * `ratePaisaPerDay` would sail straight past them and hand a Staff account
   * the daily feed rate — BR §16 leak 3, in one field name.
   */
  dailyRatePaisa: moneyPaisa(),
  /** When true the rate is scaled by each animal's `consumptionUnit`. */
  applyConsumptionUnit: z.boolean(),
  startDate: isoDateSchema,
  /** `null` is ongoing — the common case, and why the column is nullable. */
  endDate: isoDateSchema.nullish(),
  note: noteSchema.nullish(),
  createdAt: isoInstantSchema,
  version: versionSchema,
})

export const feedPlanListQuery = z.object({
  animalId: idSchema.optional(),
  /** Only the plans in force on this date. */
  activeOn: isoDateSchema.optional(),
  includeEnded: z.coerce.boolean().optional(),
})

export const createFeedPlanRequest = z
  .object({
    animalId: idSchema.optional(),
    dailyRatePaisa: positivePaisaSchema,
    applyConsumptionUnit: z.boolean().default(false),
    startDate: isoDateSchema,
    endDate: isoDateSchema.optional(),
    note: noteSchema.optional(),
  })
  .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    message: 'The rate ends before it starts.',
    path: ['endDate'],
  })

export const patchFeedPlanRequest = z
  .object({
    dailyRatePaisa: positivePaisaSchema.optional(),
    applyConsumptionUnit: z.boolean().optional(),
    startDate: isoDateSchema.optional(),
    endDate: isoDateSchema.nullish(),
    note: noteSchema.nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

export const feedPlanListResponse = z.array(feedPlanSchema)

export type FeedPlan = z.infer<typeof feedPlanSchema>
export type FeedPlanListQuery = z.input<typeof feedPlanListQuery>
export type CreateFeedPlanRequest = z.input<typeof createFeedPlanRequest>
export type PatchFeedPlanRequest = z.input<typeof patchFeedPlanRequest>
